import type { RecipeStore } from "../core/storage.ts";
import type {
  BuildRecipe,
  SavedMechSummary,
} from "../core/types.ts";

interface NamedRecipeRecord {
  readonly summary: SavedMechSummary;
  readonly recipe: BuildRecipe;
}

interface WorkspaceEnvelope {
  readonly schema: 1;
  readonly nextNamedId: number;
  readonly current: BuildRecipe | null;
  readonly named: readonly NamedRecipeRecord[];
}

const EMPTY_WORKSPACE: WorkspaceEnvelope = {
  schema: 1,
  nextNamedId: 1,
  current: null,
  named: [],
};

/** One small versioned JSON envelope; no renderer objects or asset payloads enter storage. */
export class LocalStorageRecipeStore implements RecipeStore {
  readonly #key: string;
  readonly #storage: Storage;

  constructor(namespace: string, storage: Storage = window.localStorage) {
    this.#key = `${namespace}:workspace:v1`;
    this.#storage = storage;
  }

  async readCurrent(): Promise<BuildRecipe | null> {
    return clone(this.#read().current);
  }

  async writeCurrent(recipe: BuildRecipe): Promise<void> {
    const workspace = this.#read();
    this.#write({ ...workspace, current: clone(recipe) });
  }

  async listNamed(): Promise<readonly SavedMechSummary[]> {
    return this.#read().named
      .map(({ summary }) => ({ ...summary }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  async readNamed(id: string): Promise<BuildRecipe | null> {
    const record = this.#read().named.find((candidate) => candidate.summary.id === id);
    return record == null ? null : clone(record.recipe);
  }

  async writeNamed(name: string, recipe: BuildRecipe): Promise<SavedMechSummary> {
    const workspace = this.#read();
    const existing = workspace.named.find((record) => record.summary.name === name);
    const summary = existing?.summary ?? {
      id: `saved-${workspace.nextNamedId}`,
      name,
    };
    const nextRecord = { summary: { ...summary, name }, recipe: clone(recipe) };
    const named = existing == null
      ? [...workspace.named, nextRecord]
      : workspace.named.map((record) =>
          record.summary.id === existing.summary.id ? nextRecord : record,
        );
    this.#write({
      ...workspace,
      nextNamedId: existing == null ? workspace.nextNamedId + 1 : workspace.nextNamedId,
      named,
    });
    return { ...nextRecord.summary };
  }

  #read(): WorkspaceEnvelope {
    const raw = this.#storage.getItem(this.#key);
    if (raw == null) return structuredClone(EMPTY_WORKSPACE);
    const value: unknown = JSON.parse(raw);
    if (!isWorkspaceEnvelope(value)) {
      throw new Error("Unsupported or corrupt mech constructor workspace");
    }
    return structuredClone(value);
  }

  #write(value: WorkspaceEnvelope): void {
    this.#storage.setItem(this.#key, JSON.stringify(value));
  }
}

function isWorkspaceEnvelope(value: unknown): value is WorkspaceEnvelope {
  if (value == null || typeof value !== "object") return false;
  const candidate = value as Partial<WorkspaceEnvelope>;
  return (
    candidate.schema === 1 &&
    Number.isSafeInteger(candidate.nextNamedId) &&
    Array.isArray(candidate.named) &&
    (candidate.current === null || typeof candidate.current === "object")
  );
}

function clone<T>(value: T): T {
  return value == null ? value : structuredClone(value);
}
